import { displayLocale } from './i18n.ts';

/*
 * The comparison a sortable table uses, in a `.ts` file of its own.
 *
 * Not in `ui.tsx` with the hook that calls it: the package's test runner
 * strips types from `.ts` and cannot load `.tsx` at all, so a rule that lives
 * beside JSX is a rule that cannot be tested here. This one is worth testing —
 * see test/sort-rows.test.ts.
 */
export type SortDir = 'asc' | 'desc';
export type SortValue<Row> = (row: Row) => string | number | null | undefined;

/**
 * The comparison itself, outside the hook so it can be proven without a
 * renderer.
 *
 * Empty last in **both** directions — a row with no figure is not the smallest
 * figure, and reversing the sort to hunt for one is not something anybody sets
 * out to do. This was `sortQualificationByLink` in the affiliate console,
 * which had the rule and one table; it is every table's now.
 *
 * `numeric` on the text path so "Sub ID 2" sorts before "Sub ID 10", and the
 * reader's own locale so an accented name files where they expect it.
 */
export function sortRows<Row>(
  rows: readonly Row[],
  value: SortValue<Row>,
  dir: SortDir,
): readonly Row[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    const xEmpty = x === null || x === undefined || x === '';
    const yEmpty = y === null || y === undefined || y === '';
    if (xEmpty && yEmpty) return 0;
    if (xEmpty) return 1;
    if (yEmpty) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * factor;
    return String(x).localeCompare(String(y), displayLocale(), { numeric: true }) * factor;
  });
}


/**
 * What a formatted table cell sorts on.
 *
 * Most view models hand the screen strings that are already formatted for
 * reading — "2,000.00 NGN", "−354.00 NGN", "65%", "1,234". Ordered as text
 * those go wrong in exactly the way a reader notices: "2,000.00" lands before
 * "900.00" because "2" is less than "9". This pulls the number back out so a
 * column of money or counts orders by its value, and leaves everything else to
 * sort as the text it is.
 *
 * A null or an empty cell stays null, which `sortRows` puts last either way.
 */
export function sortableCell(value: unknown): string | number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const text = String(value).trim();
  if (!text || text === '—') return null;
  // Digits with grouping, a decimal, a sign (ASCII or typographic minus) and
  // an optional trailing unit or percent — the shapes this platform prints.
  const m = /^([−-]?)\s*([\d,]+(?:\.\d+)?)\s*(?:[A-Z]{3}|%)?$/.exec(text);
  if (m) {
    const n = Number(m[2]!.replace(/,/g, ''));
    if (Number.isFinite(n)) return m[1] ? -n : n;
  }
  return text;
}
